/**
 * Client di streaming per la chat con Aida (Fase B1).
 *
 * Usa `fetch` + `ReadableStream` (NON EventSource: serve passare gli header
 * custom di auth — X-Stub-Email/Bearer/X-Vedi-Come — che EventSource non
 * supporta). Il backend risponde `text/event-stream` con frame `data: {...}`.
 */

import { API_BASE, authHeaders } from "@/lib/api";

export interface AzioneUi {
  tipo: string;
  cliente?: string;
  id?: string;
  [k: string]: unknown;
}

export type ChatEvent =
  | { tipo: "delta"; testo: string }
  | { tipo: "tool"; nome: string }
  // La risposta ha attraversato un altro agente: «ho chiesto a Iris».
  | { tipo: "fonte"; testo: string }
  | { tipo: "azione_ui"; azioni: AzioneUi[] }
  | { tipo: "fine"; conversazione_id: string; messaggio_id: string; titolo: string | null }
  | { tipo: "errore"; messaggio: string };

/**
 * Invia un turno di chat e invoca `onEvent` per ogni evento dello stream.
 * `conversazioneId` assente → nuova conversazione (l'id arriva nell'evento fine).
 * `contestoDomandaId` = la chat è stata aperta da un «?» del tabellone: l'id
 * viaggia col turno così l'agente sa quale domanda chiudere invece di dedurlo.
 * Risolve quando lo stream è completo; rigetta su errore di rete/HTTP.
 */
export async function streamChat(
  testo: string,
  conversazioneId: string | null,
  onEvent: (ev: ChatEvent) => void,
  signal?: AbortSignal,
  basePath: string = "/api/v1/m3/agente/chat",
  contestoDomandaId?: string | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}${basePath}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(await authHeaders()),
    },
    body: JSON.stringify({
      testo,
      conversazione_id: conversazioneId,
      ...(contestoDomandaId ? { contesto_domanda_id: contestoDomandaId } : {}),
    }),
    signal,
  });

  if (!res.ok) {
    let dettaglio = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) dettaglio = String(body.detail);
    } catch {
      // corpo non-JSON: tieni il codice HTTP
    }
    throw new Error(dettaglio);
  }
  if (!res.body) {
    throw new Error("Risposta senza corpo dallo stream.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const payload = dataLine.slice("data:".length).trim();
      if (!payload) continue;
      try {
        onEvent(JSON.parse(payload) as ChatEvent);
      } catch {
        // frame malformato: ignora
      }
    }
  }
}

/** Sintetizza una frase in audio (MP3) via il proxy ElevenLabs del backend. */
export async function sintetizzaVoce(
  testo: string,
  basePath: string = "/api/v1/m3/agente/chat",
): Promise<Blob> {
  const res = await fetch(`${API_BASE}${basePath}/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "audio/mpeg",
      ...(await authHeaders()),
    },
    body: JSON.stringify({ testo }),
  });
  if (!res.ok) {
    throw new Error(`TTS HTTP ${res.status}`);
  }
  return res.blob();
}
